/**
 * Ticket 028. If the loop is audible and the visitor presses an object, the
 * loop mutes: every object worth pressing either starts a competing sound (the
 * City of Thirst track, the music video, a YouTube embed) or takes the visitor
 * off the page entirely (a social profile, a mail client, Bandsintown).
 *
 * One delegated listener rather than a handler per object, for two reasons: no
 * element changes, so no pixel moves, and the next drawing added to the site
 * gets the behaviour without anyone remembering to wire it. The short list is
 * the exceptions, and they are marked `data-audio="keep"` in the JSX where the
 * element is written — the menu row and the nav, the wordmark, the toggle
 * itself, the whole signup flow, and the two figures on the landing page,
 * whose press flips a pose and starts nothing.
 *
 * One hole this cannot close: `#tour-dates-frame` is a cross-origin Bandsintown
 * iframe (ticket 011), so a press on a tour date happens inside a document we
 * cannot listen to and the loop plays on. The heuristics that would cover it —
 * `blur`, `visibilitychange`, a `pointerdown` on the iframe's own box — all
 * fire on things that are not presses, and would mute a visitor who scrolled.
 */

/** Tags that are an object by themselves, whatever classes they wear. */
const OBJECT_TAGS = new Set(["A", "BUTTON", "VIDEO"]);

/** ...and the two classes the drawings use: the cursor and the hit target. */
const OBJECT_CLASSES = ["interactive", "hitbox", "video-item"];

/**
 * `.video-item` above and `#video-thumb` here are the two objects that are
 * neither a link nor a marked drawing — a bare `<div>` and a bare `<img>` with
 * an `onClick` that starts a YouTube player. They are named because they are
 * exactly the presses this rule exists for: an embed starting under the loop.
 */
const OBJECT_IDS = ["video-thumb"];

/**
 * The part of `Element` the walk touches, so a test can hand-build a chain
 * without a DOM — `bun test` has none, and this is the file where the rule
 * about what counts as an object actually lives.
 */
export type PressNode = {
  readonly tagName: string;
  readonly classList: { contains(token: string): boolean };
  getAttribute(name: string): string | null;
  readonly parentElement: PressNode | null;
};

function asNode(target: unknown): PressNode | null {
  if (!target || typeof target !== "object") return null;
  const node = target as Partial<PressNode>;
  return typeof node.tagName === "string" ? (node as PressNode) : null;
}

function isObject(node: PressNode): boolean {
  if (OBJECT_TAGS.has(node.tagName.toUpperCase())) return true;
  if (OBJECT_CLASSES.some((name) => node.classList.contains(name))) return true;
  return OBJECT_IDS.includes(node.getAttribute("id") ?? "");
}

/**
 * True if this press was a press on an object, and on nothing opted out.
 *
 * The whole chain is walked even after an object is found: the opt-out sits on
 * the region — `.site-nav`, `#signup-row` — and the object is the anchor
 * *inside* it, so an exemption above the object still exempts.
 */
export function shouldMuteFor(target: unknown): boolean {
  let node = asNode(target);
  let object = false;
  while (node) {
    if (node.getAttribute("data-audio") === "keep") return false;
    if (!object && isObject(node)) object = true;
    node = asNode(node.parentElement);
  }
  return object;
}

type PressHost = {
  addEventListener(
    type: "click",
    handler: (event: { target: unknown }) => void,
    capture: boolean,
  ): void;
  removeEventListener(
    type: "click",
    handler: (event: { target: unknown }) => void,
    capture: boolean,
  ): void;
};

/**
 * Capture phase on the document, which is ahead of React's own delegated
 * handlers (they attach at the root container, bubble phase). That ordering is
 * the point: the loop is muted before the pressed object mounts its YouTube
 * iframe or swaps in an autoplaying embed, so the two never overlap.
 */
export function listenForPresses(host: PressHost, mute: () => void): () => void {
  const onPress = (event: { target: unknown }) => {
    if (shouldMuteFor(event.target)) mute();
  };
  host.addEventListener("click", onPress, true);
  return () => host.removeEventListener("click", onPress, true);
}
